import { memo } from "react";
import toast from "react-hot-toast";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { useAtomValue } from "jotai";

import { useTranslation } from "@arc/i18n";
import { useTonWallet } from "@arc/sdk";
import { SCROLL_CONTAINER_ID } from "@arc/shared/constants/ui";
import { cn } from "@arc/ui/cn";
import { BankIcon } from "@arc/ui/icons/bank";
import { BriefcaseIcon } from "@arc/ui/icons/briefcase";
import { Convert3DCubeIcon } from "@arc/ui/icons/convert-3d-cube";
import { KeySquareIcon } from "@arc/ui/icons/key-square";
import { UserIcon } from "@arc/ui/icons/user";
import { Wallet2Icon } from "@arc/ui/icons/wallet-2";
import { Menu } from "@arc/ui/menu";

import { showCameraAtom, showMenuAtom } from "@/atoms/ui";
import { AppRoute, BankRoute } from "@/constants/routes";
import { usePlatformFeature } from "@/hooks/platform";

import BackButton, { backButtonExclude } from "./BackButton";
import { MainButton } from "./MainButton";
import { StyledToaster } from "./Toaster";
import { NoWalletErrorToast } from "./Toasts/NoWalletErrorToast";

export const Layout: React.FC = memo(() => {
  const { i18n, t } = useTranslation("wallet");
  const navigate = useNavigate();
  const location = useLocation();
  const showMenu = useAtomValue(showMenuAtom);
  const showCamera = useAtomValue(showCameraAtom);
  const activeWallet = useTonWallet();
  const { usePlatformData, hapticFeedback } = usePlatformFeature();
  const { platform } = usePlatformData();

  // TODO: Придумать куда это вынести
  const items = [
    {
      label: t("MENU.WALLET"),
      icon: <Wallet2Icon />,
      onClick: () => {
        hapticFeedback("medium");
        navigate(AppRoute.wallet);
      },
      active:
        location.pathname.startsWith(AppRoute.wallet) ||
        location.pathname.startsWith(AppRoute.send) ||
        location.pathname.startsWith(AppRoute.deposit),
      disabled: location.pathname === AppRoute.wallet,
    },
    {
      label: t("MENU.APPS"),
      icon: <BriefcaseIcon />,
      onClick: () => {
        hapticFeedback("medium");
        navigate(AppRoute.catalog);
      },
      active: location.pathname.startsWith(AppRoute.catalog),
      disabled: location.pathname === AppRoute.catalog,
    },
    {
      label: t("MENU.MARKET"),
      icon: <Convert3DCubeIcon />,
      onClick: () => {
        hapticFeedback("medium");
        navigate(AppRoute.market);
      },
      active: location.pathname.startsWith(AppRoute.market),
      disabled: location.pathname === AppRoute.market,
    },
    {
      label: t("MENU.BUY"),
      icon: <BankIcon />,
      onClick: async () => {
        hapticFeedback("medium");
        if (!activeWallet) {
          await i18n.loadNamespaces("bank");
          toast(<NoWalletErrorToast onClick={() => toast.dismiss("buy-bank")} />, {
            id: "buy-bank",
            duration: 10000,
            icon: <KeySquareIcon className="stroke-accent h-8 w-8 fill-none" />,
          });
        } else {
          navigate(BankRoute.buy);
        }
      },
      active: location.pathname.startsWith(AppRoute.bank),
      disabled: location.pathname === AppRoute.bank,
    },
    {
      label: t("MENU.ACCOUNT"),
      icon: <UserIcon />,
      onClick: () => {
        hapticFeedback("medium");
        navigate(AppRoute.settings);
      },
      active: location.pathname.startsWith(AppRoute.settings),
      disabled: location.pathname === AppRoute.settings,
    },
  ];

  const safeAreaTop =
    platform === "android" || platform === "ios" ? { paddingTop: "calc(env(safe-area-inset-top) + 1rem)" } : {};

  const safeAreaBottom =
    platform === "android" || platform === "ios" ? { paddingBottom: "env(safe-area-inset-bottom)" } : {};

  const safeMenuAreaBottom =
    platform === "android" || platform === "ios"
      ? { marginBottom: "calc(env(safe-area-inset-bottom) + 4rem)" }
      : { marginBottom: "4rem" };

  if (showCamera) {
    return (
      <div className="flex h-screen flex-col overflow-hidden" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <StyledToaster />
        <div className="text-primary py-6 text-center text-2xl font-bold">Scan QR Code</div>
      </div>
    );
  }

  return (
    <div className="bg-background flex h-screen flex-col overflow-hidden">
      <StyledToaster />
      <div
        className={cn("flex-grow overflow-y-auto")}
        id={SCROLL_CONTAINER_ID}
        style={showMenu ? safeMenuAreaBottom : {}}
      >
        <div
          className={cn({
            "m-auto box-border flex h-full w-full flex-col p-4": true,
            "mt-10": (platform === "android" || platform === "ios") && !backButtonExclude.includes(location.pathname),
            // "pt-[env(safe-area-inset-top)]": platform === "android" || platform === "ios",
            "bg-background mx-auto max-w-[480px]": platform === "web",
          })}
          style={safeAreaTop}
        >
          <Outlet />
        </div>
      </div>

      {showMenu && (
        <div
          className={cn(
            "border-separator bg-background fixed bottom-0 left-0 right-0 z-50 border-t-2",
            platform === "web" ? "mx-auto max-w-[480px]" : "",
          )}
          style={safeAreaBottom}
        >
          <Menu items={items} standalone={platform === "ios"} />
        </div>
      )}

      <MainButton />
      <BackButton />
    </div>
  );
});

Layout.displayName = "Layout";
